import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { StyledMenu } from '@ui/components/common/Menu';
import MoveToVehicleMenu from './MoveToVehicleMenu';
const VehicleAttendeesDialog = ({ open, onClose, rows, moveToVehicleOptions, onMoveToVehicle, loading = false, isFamilyFest = false, onFetchVehiclesForAttendee }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
    const [activeRowId, setActiveRowId] = useState(null);
    const [moveMenuPosition, setMoveMenuPosition] = useState(null);
    const [selectedMoveVehicleId, setSelectedMoveVehicleId] = useState(null);
    const [filteredMoveOptions, setFilteredMoveOptions] = useState(null);
    const rowMenuActions = useMemo(() => [
        {
            text: t('events.eventDetails.transportTab.moveToVehicle'),
            group: true,
            to: () => {
                if (rowMenuAnchorEl) {
                    const rect = rowMenuAnchorEl.getBoundingClientRect();
                    setMoveMenuPosition({
                        top: Math.round(rect.bottom + 4),
                        left: Math.round(rect.right)
                    });
                    setSelectedMoveVehicleId(null);
                }
                setRowMenuAnchorEl(null);
            }
        }
    ], [rowMenuAnchorEl, t]);
    const columns = [
        {
            field: 'name',
            headerName: t('labels.name'),
            flex: 1.2,
            minWidth: 170,
            sortable: false,
            renderCell: (params) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40, children: [_jsx(Typography, { variant: 'body2', children: params.row.name }), params.row.isBirthday && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: t('people.birthday') }))] }))
        },
        {
            field: 'company',
            headerName: t('labels.company'),
            flex: 1,
            minWidth: 130,
            sortable: false,
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', noWrap: true, style: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: params.row.company || '-' }))
        },
        {
            field: 'type',
            headerName: t('common.type'),
            flex: 0.9,
            minWidth: 110,
            sortable: false
        },
        {
            field: 'primaryPerson',
            headerName: t('events.eventDetails.transportTab.primaryPerson'),
            flex: 1,
            minWidth: 140,
            sortable: false
        },
        {
            field: 'registered',
            headerName: t('events.eventDetails.transportTab.registered'),
            flex: 1,
            minWidth: 130,
            sortable: false
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.45,
            minWidth: 70,
            sortable: false,
            filterable: false,
            renderCell: (params) => (_jsx(IconButton, { size: 'small', onClick: (event) => {
                    event.stopPropagation();
                    setActiveRowId(params.row.id);
                    setRowMenuAnchorEl(event.currentTarget);
                    if (isFamilyFest && onFetchVehiclesForAttendee && params.row.pickupLocationId) {
                        onFetchVehiclesForAttendee(params.row.pickupLocationId).then(setFilteredMoveOptions);
                    }
                }, children: _jsx(MoreHoriz, { fontSize: 'small' }) }))
        }
    ];
    const content = (_jsxs(Box, { children: [loading && (_jsx(Box, { display: 'flex', justifyContent: 'center', py: 4, children: _jsx(CircularProgress, { size: 32 }) })), !loading && (_jsx(PrimaryGrid, { columns: columns, rows: rows, showHeaderControls: false, showNewHeaderControls: false, noRowsText: t('labels.noOptions'), maxHeight: '360px' })), _jsx(StyledMenu, { anchorEl: rowMenuAnchorEl, open: Boolean(rowMenuAnchorEl), onClose: () => {
                    setRowMenuAnchorEl(null);
                }, actions: rowMenuActions, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }), _jsx(MoveToVehicleMenu, { anchorPosition: moveMenuPosition, open: Boolean(moveMenuPosition), onClose: () => {
                    setMoveMenuPosition(null);
                    setSelectedMoveVehicleId(null);
                    setFilteredMoveOptions(null);
                }, options: filteredMoveOptions ?? moveToVehicleOptions, selectedVehicleIds: selectedMoveVehicleId ? [selectedMoveVehicleId] : [], onSelect: (vehicle) => {
                    setSelectedMoveVehicleId(vehicle.id === selectedMoveVehicleId ? null : vehicle.id);
                }, onConfirm: () => {
                    if (!activeRowId || !selectedMoveVehicleId)
                        return;
                    const effectiveOptions = filteredMoveOptions ?? moveToVehicleOptions;
                    const target = effectiveOptions.find((v) => v.id === selectedMoveVehicleId);
                    if (target?.routeVehicleId) {
                        onMoveToVehicle?.(activeRowId, target.routeVehicleId);
                    }
                    setMoveMenuPosition(null);
                    setSelectedMoveVehicleId(null);
                    setFilteredMoveOptions(null);
                    setActiveRowId(null);
                } })] }));
    const actions = (_jsx(Box, { width: '100%', children: _jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: onClose, fullWidth: true, children: t('actions.close') }) }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { open: open, onClose: onClose, title: t('events.eventDetails.transportTab.attendees'), anchor: 'bottom', showBottomBar: true, dialogActions: actions, children: _jsx(Box, { sx: { p: 2 }, children: content }) }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: onClose, titleWithIcon: t('events.eventDetails.transportTab.attendees'), titleIcon: '/assets/images/add-person.png', maxWidth: 'md', backgroundVariant: 'white', closeIcon: true, actions: actions, children: _jsx(Box, { p: 1.5, children: content }) }));
};
export default VehicleAttendeesDialog;
